/**
 * v-can Directive
 *
 * Permission-based element visibility directive.
 * Uses the usePermissions composable internally.
 *
 * Usage:
 * <button v-can="'employees.create'">Create Employee</button>
 * <div v-can:any="['employees.view.all', 'employees.view.team']">...</div>
 * <div v-can:all="['employees.view', 'employees.edit']">...</div>
 *
 * Modifiers:
 * - v-can:any - User must have ANY of the permissions
 * - v-can:all - User must have ALL of the permissions
 * - v-can (default) - Single permission check
 */

import { usePermissions } from '../composables/usePermissions';

export default {
    mounted(el, binding) {
        const { can, canAny, canAll } = usePermissions();

        let hasPermission = false;
        const value = binding.value;

        // Handle different argument types
        if (binding.arg === 'any') {
            // v-can:any="['perm1', 'perm2']"
            hasPermission = Array.isArray(value) ? canAny(value) : can(value);
        } else if (binding.arg === 'all') {
            // v-can:all="['perm1', 'perm2']"
            hasPermission = Array.isArray(value) ? canAll(value) : can(value);
        } else {
            // v-can="'permission'"
            hasPermission = can(value);
        }

        // Hide element if no permission
        if (!hasPermission) {
            // Store original display value
            el.style.display = 'none';
            el.setAttribute('data-v-can-hidden', 'true');
        }
    },

    updated(el, binding) {
        const { can, canAny, canAll } = usePermissions();

        let hasPermission = false;
        const value = binding.value;

        if (binding.arg === 'any') {
            hasPermission = Array.isArray(value) ? canAny(value) : can(value);
        } else if (binding.arg === 'all') {
            hasPermission = Array.isArray(value) ? canAll(value) : can(value);
        } else {
            hasPermission = can(value);
        }

        // Update visibility
        if (hasPermission) {
            if (el.getAttribute('data-v-can-hidden') === 'true') {
                el.style.display = '';
                el.removeAttribute('data-v-can-hidden');
            }
        } else {
            el.style.display = 'none';
            el.setAttribute('data-v-can-hidden', 'true');
        }
    }
};
